// Spec 45 component-behavior — inline alert (cmp_ds_banner is the
// tenant-wide variant; Alert is the surface-local one).
//
// 4 severities mirroring spec 49 ToastSeverity:
//   info / success / warning / danger
//
// a11y: severity drives the live-region role.
//   * info / success → role='status' + aria-live='polite'
//     (announce when convenient; user is not interrupted)
//   * warning / danger → role='alert' + aria-live='assertive'
//     (interrupt — user MUST hear this immediately; assertive is
//     reserved for genuinely time-sensitive content per WAI-ARIA)
//
// Inline alerts are non-dismissible by default (they describe a
// persistent condition); pass onDismiss to surface a close button.

import { forwardRef, type HTMLAttributes, type MouseEvent, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export type AlertSeverity = "info" | "success" | "warning" | "danger";

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  readonly severity: AlertSeverity;
  readonly title?: string;
  readonly children?: ReactNode;
  /** When provided, renders a close button + fires this on activation. */
  readonly onDismiss?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Override the dismiss button's accessible label. Defaults to "Dismiss alert". */
  readonly dismissLabel?: string;
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      severity,
      title,
      onDismiss,
      dismissLabel = "Dismiss alert",
      children,
      className,
      ...rest
    },
    ref,
  ) => {
    const isUrgent = severity === "warning" || severity === "danger";
    return (
      <div
        ref={ref}
        role={isUrgent ? "alert" : "status"}
        aria-live={isUrgent ? "assertive" : "polite"}
        data-component="alert"
        data-severity={severity}
        className={cx("nai-alert", className)}
        {...rest}
      >
        {title !== undefined && (
          <div data-component="alert-title" className="nai-alert-title">
            {title}
          </div>
        )}
        {children !== undefined && (
          <div data-component="alert-body" className="nai-alert-body">
            {children}
          </div>
        )}
        {onDismiss !== undefined && (
          <button
            type="button"
            data-component="alert-dismiss"
            aria-label={dismissLabel}
            className="nai-alert-dismiss"
            onClick={onDismiss}
          >
            ×
          </button>
        )}
      </div>
    );
  },
);
Alert.displayName = "Alert";
